import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { tx } from "@/i18n";

const euro = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(n || 0);

export function NightFeeEditor({ item, onSaved }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(String(item.amount));
  const [busy, setBusy] = useState(false);

  const save = async (amount) => {
    setBusy(true);
    try {
      await api.patch(`/bookings/${item.booking_id}/night-fee`, { amount });
      toast.success(tx("Nachttoeslag aangepast"));
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setVal(String(item.amount)); }}>
      <PopoverTrigger asChild>
        <button type="button" className="p-1 text-muted-foreground hover:text-primary" title={tx("Nachttoeslag aanpassen")} data-testid={`night-fee-edit-${item.booking_id}`}>
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-2.5" data-testid={`night-fee-popover-${item.booking_id}`}>
        <p className="text-sm font-semibold">{tx("Nachttoeslag aanpassen")}</p>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">€</span>
          <Input type="number" min="0" step="0.01" inputMode="decimal" value={val} onChange={(e) => setVal(e.target.value)} className="pl-7" data-testid={`night-fee-input-${item.booking_id}`} />
        </div>
        <div className="flex gap-2">
          <Button size="sm" className="flex-1" disabled={busy || val === "" || Number(val) < 0} onClick={() => save(Number(val))} data-testid={`night-fee-save-${item.booking_id}`}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />} {tx("Opslaan")}
          </Button>
          <Button size="sm" variant="outline" className="flex-1 text-destructive" disabled={busy} onClick={() => save(0)} data-testid={`night-fee-waive-${item.booking_id}`}>{tx("Laten vervallen")}</Button>
        </div>
        {item.custom && (
          <button type="button" className="text-xs text-muted-foreground underline" disabled={busy} onClick={() => save(null)} data-testid={`night-fee-reset-${item.booking_id}`}>
            {tx("Terug naar standaard")} ({euro(item.default_amount)})
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
